/// <amd-module name="Widgets/VelocityWidget/VelocityWidgetComponents/VelocityDataManager" />
import { Iteration } from "Analytics/Boards/BoardsQueries/IterationsQuery";
import { IterationsQuery } from "Analytics/Boards/BoardsQueries/IterationsQuery";
import { CoreFieldRefNames } from "WidgetCommon/WitUtils/Constants";

import { DemandType } from "Reporting/Visuals/LayoutComponents/Widgets/Demands/DemandType";
import { LayoutState, MessageType } from "Reporting/Visuals/LayoutComponents/Widgets/LayoutState";

import { AggregationWorkItemTypeFieldsQuery } from "Analytics/Boards/VelocityQueries/AggregationWorkItemTypeFieldsQuery";
import { CompletedLateMetastateByIterationDetailsQuery } from "Analytics/Boards/VelocityQueries/CompletedLateMetastateByIterationDetailsQuery";
import { CompletedLateMetastateByIterationsQuery } from "Analytics/Boards/VelocityQueries/CompletedLateMetastateByIterationsQuery";
import { MetastateByIterationDetailsQuery } from "Analytics/Boards/VelocityQueries/MetastateByIterationDetailsQuery";
import { MetastateByIterationsQuery } from "Analytics/Boards/VelocityQueries/MetastateByIterationsQuery";
import { PlannedWorkByDayDetailsQuery } from "Analytics/Boards/VelocityQueries/PlannedWorkByDayDetailsQuery";
import { PlannedWorkByDayQuery } from "Analytics/Boards/VelocityQueries/PlannedWorkByDayQuery";
import * as WidgetTelemetry from "Dashboards/WidgetCommon/WidgetTelemetry";
import { WidgetTeamContext } from "Dashboards/WidgetContracts/WidgetProps";
import { AggregationMode } from "WidgetCommon/ModernWidgetTypes/CommonConfigurationTypes";
import { ModefulValueSetting } from "WidgetCommon/ModernWidgetTypes/ModefulValueSetting";
import { WidgetDataManagerBase, WidgetDataManagerOptions } from "WidgetCommon/ModernWidgetTypes/WidgetDataManagerBase";
import { WiqlURLBuilder } from "WidgetCommon/WitUtils/WiqlUrlBuilder";
import * as Resources from "Widgets/VelocityWidget/Resources";
import {
    IHandleVelocityChartEvents,
    VelocityChartClickEventData,
    VelocityChartInputs,
    VelocityChartOptionFactory
} from "./VelocityChartOptionFactory";
import { VelocityConstants } from "./VelocityConstants";
import { WorkItemTypeFilterMode } from "./VelocityDataContract";
import { Metastate, Work, WorkItem, WorkItemTypeField, WorkItemWithState } from "./VelocityDataContract";
import { VelocityDataHelper } from "./VelocityDataHelper";
import { VelocitySettings } from "./VelocitySettings";

import { ProcessesQuery } from "Analytics/Boards/BoardsQueries/ProcessesQuery";
import { AnalyticsCacheableQueryBase } from "Analytics/Common/AnalyticsCacheableQueryBase";
import { AnalyticsExceptionType, extractODataError, recognizeAnalyticsException } from "Analytics/Common/Utilities/AnalyticsExceptionUtilities";
import { ICacheableQueryService, IDashboardsCacheableQueryServiceName } from "Dashboards/WidgetCommon/DashboardsCacheableQueryService";
import { format } from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";

/**
 * Responsible for heuristically optimizing for the lowest cost set of query implementations for Velocity data requirements.
 */
export class VelocityDataManager extends WidgetDataManagerBase implements IHandleVelocityChartEvents {
    private dataService: ICacheableQueryService;
    private teamContext: WidgetTeamContext;

    // Encapsulates state for downstream querying on click events. This needs to be factored away from here.
    private queryContext: { velocitySettings: VelocitySettings; workItemTypes: string[] };

    constructor(context: IVssPageContext, options: WidgetDataManagerOptions, protected command: string = "Velocity") {
        super(context, options);
        this.dataService = this.context.getService<ICacheableQueryService>(IDashboardsCacheableQueryServiceName);
        this.teamContext = options.widgetTelemetryProps.teamContext;
    }

    /** Start running any demands which require async behavior. */
    public async getData(): Promise<LayoutState> {
        const dataQualityWarnings: Set<string> = new Set<string>();

        // Note: Scalar, subtitle and chart all rely on results from the same trend query. Scalar provides an average sum/count across iterations, and subtitle provides a count of the number of iterations we saw.
        // With this requirement - there is no substantial optimizations to distinguish the queries for those demands..
        if (
            !this.demandTracker.isDemandPresent(DemandType.subtitle) &&
            !this.demandTracker.isDemandPresent(DemandType.scalar) &&
            !this.demandTracker.isDemandPresent(DemandType.chart)
        ) {
            return Promise.resolve(this.currentState);
        } else {
            // Only implementing general chart demand, at this stage.
            const velocitySettings = JSON.parse(this.settings || "") as VelocitySettings;
            const teamId = velocitySettings.teamId;
            const projectId = velocitySettings.projectId;
            const desiredIterationCount = velocitySettings.numberOfIterations;
            const aggregation: ModefulValueSetting<AggregationMode, string> = velocitySettings.aggregation as ModefulValueSetting<
                AggregationMode,
                string
            >;
            if (!aggregation.settings) {
                aggregation.settings = "";
            }

            // Override value to false if FF is disabled
            if(!isFeatureFlagEnabled(this.context,VelocityConstants.featureFlagName_ShowResolvedItemsAsCompleted, false)){
                velocitySettings.showResolvedItemsAsCompleted = false;
            }

            const iterationsQueryPromise = this.getIterations(projectId, teamId, desiredIterationCount, dataQualityWarnings);
            return this.getWorkItemTypes(velocitySettings)
                .then(workItemTypes => {
                    return iterationsQueryPromise.then(async (iterations: Iteration[]) => {
                        if (iterations && iterations.length > 0) {
                            const iterationIds: string[] = [];
                            iterations
                                .filter(x => x.IterationSK)
                                .map((iteration: Iteration) => {
                                    iterationIds.push(iteration.IterationSK);
                                });

                            // Metastate
                            const metastatePromise = this.getMetastate(
                                iterationIds,
                                projectId,
                                teamId,
                                aggregation,
                                workItemTypes,
                                dataQualityWarnings
                            );

                            // Late work (if delay is undefined, the feature is off... don't perform the query and instead return a promise resolved to null)
                            const completedLateMetastatePromise =
                                velocitySettings.lateWorkDelay != null
                                    ? this.getCompletedLateMetastate(
                                          iterations,
                                          projectId,
                                          teamId,
                                          aggregation,
                                          workItemTypes,
                                          velocitySettings.showResolvedItemsAsCompleted,
                                          velocitySettings.lateWorkDelay,
                                          dataQualityWarnings
                                      )
                                    : Promise.resolve(undefined);

                            // Planned work (if delay is undefined, the feature is off... don't perform the query and instead return a promise resolved to null)
                            const plannedWorkByDayPromise =
                                velocitySettings.plannedWorkDelay != null
                                    ? this.getPlannedWorkByDay(
                                          projectId,
                                          teamId,
                                          iterations,
                                          aggregation,
                                          workItemTypes,
                                          velocitySettings.plannedWorkDelay,
                                          dataQualityWarnings
                                      )
                                    : Promise.resolve(undefined);

                            // AggregationWorkItemTypeField
                            const aggregationWorkItemTypeFieldsPromise = this.getAggregationWorkItemTypeFields(projectId);

                            const [
                                metastateQueryResult,
                                completedLateQueryResult,
                                plannedWorkByDayQueryResult,
                                aggregationWorkItemTypeFieldResult
                            ] = await Promise.all([
                                metastatePromise,
                                completedLateMetastatePromise,
                                plannedWorkByDayPromise,
                                aggregationWorkItemTypeFieldsPromise
                            ]);

                            if (metastateQueryResult!.length === 0) {
                                return this.packMessageAsState(MessageType.NoData);
                            }

                            this.queryContext = {
                                velocitySettings: velocitySettings,
                                workItemTypes: workItemTypes
                            };

                            const chartInputs: VelocityChartInputs = {
                                velocityClickHandler: this,

                                iterations: iterations,
                                metastateResult: metastateQueryResult as Metastate[],
                                plannedWorkByDayResult: plannedWorkByDayQueryResult,
                                completedLateResult: completedLateQueryResult as Metastate[],

                                suppressAnimations: this.suppressAnimations,
                                isAdvancedChart: velocitySettings.plannedWorkDelay != null,
                                showResolvedItemsAsCompleted: velocitySettings.showResolvedItemsAsCompleted ?? false
                            };
                            const chartOptions = await new VelocityChartOptionFactory(this.context).createChartOptions(chartInputs);

                            this.currentState = {
                                showMessage: false,
                                title: { text: this.title },
                                subtitle: { text: VelocityDataHelper.getSubtitle(iterations.length) },
                                scalarData: {
                                    description: Resources.VelocityWidget_AverageVelocity,
                                    measure: VelocityDataHelper.getScalarMeasure(aggregationWorkItemTypeFieldResult as WorkItemTypeField[], aggregation),
                                    value:
                                        metastateQueryResult && metastateQueryResult.length > 0
                                            ? VelocityDataHelper.getAverageVelocity(metastateQueryResult as Metastate[], iterations, velocitySettings.showResolvedItemsAsCompleted)
                                            : 0
                                },
                                chartData: {
                                    chartOptions: chartOptions
                                },
                                warningsData: Array.from(dataQualityWarnings)
                            };

                            return this.currentState;
                        } else {
                            // No iteration data available - set iteration dates.
                            return this.packMessageAsState(MessageType.SetIterationDates);
                        }
                    });
                })
                .then(null, error => {
                    // Catch pattern
                    let messageType = MessageType.WidgetError;
                    // Check if we should show fault-in instead
                    if (recognizeAnalyticsException(error) === AnalyticsExceptionType.DataNotReady) {
                        messageType = MessageType.AxFaultIn;
                    }
                    if (recognizeAnalyticsException(error) === AnalyticsExceptionType.MissingAnalyticsPermissions) {
                        messageType = MessageType.MissingPermissions;
                    }

                    const errorMessage = extractODataError(error);
                    return this.packMessageAsState(messageType, errorMessage);
                });
        }
    }

    public handleLegendClick(): void {
        WidgetTelemetry.onWidgetClick(this.context, this.widgetPropsForTelemetry, { linkDescription: "LegendClicked" });
    }

    public handleChartClick(clickEvent: VelocityChartClickEventData, dataQualityWarnings?: Set<string>): void {
        // If the clicked point has no state, no-op on handling the click.
        if (clickEvent.valueAtPoint) {
            // Some browser popup blockers do not allow opening a new window if it is not performed on the main thread
            // of execution resulting from a user action. To circumvent this we open a window on the main thread and
            // assign a new URL to it later once we've constructed it.
            const tempWindow = window.open();
            let promise: Promise<WorkItem[]> | null;

            switch (clickEvent.stateName) {
                case Resources.VelocityChart_Planned_StateName:
                    promise = this.getPlannedWorkByDayDetails(
                        this.queryContext.velocitySettings.projectId,
                        this.queryContext.velocitySettings.teamId,
                        clickEvent.iteration,
                        this.queryContext.workItemTypes,
                        this.queryContext.velocitySettings.plannedWorkDelay as number
                    );
                    break;
                case Resources.VelocityChart_CompletedLate_StateName:
                    promise = this.getCompletedLateMetastateDetails(
                        this.queryContext.velocitySettings.projectId,
                        this.queryContext.velocitySettings.teamId,
                        clickEvent.iteration,
                        this.queryContext.workItemTypes,
                        this.queryContext.velocitySettings.showResolvedItemsAsCompleted,
                        this.queryContext.velocitySettings.lateWorkDelay as number,
                        dataQualityWarnings
                    );
                    break;
                case Resources.VelocityChart_Completed_StateName:
                    promise = this.getCompletedMetastateDetails(
                        this.queryContext.velocitySettings.projectId,
                        this.queryContext.velocitySettings.teamId,
                        clickEvent.iteration,
                        this.queryContext.workItemTypes,
                        this.queryContext.velocitySettings.showResolvedItemsAsCompleted,
                        this.queryContext.velocitySettings.lateWorkDelay,
                        dataQualityWarnings
                    );
                    break;
                case Resources.VelocityChart_Incomplete_StateName:
                    promise = this.getIncompleteMetastateDetails(
                        this.queryContext.velocitySettings.projectId,
                        this.queryContext.velocitySettings.teamId,
                        clickEvent.iteration,
                        this.queryContext.workItemTypes,
                        this.queryContext.velocitySettings.showResolvedItemsAsCompleted,
                        dataQualityWarnings
                    );
                    break;
                default:
                    promise = null;
                    break;
            }

            if (promise != null) {
                promise
                    .then((workItems: WorkItem[]) => {
                        const workItemIds = workItems.map(wi => wi.WorkItemId);
                        WidgetTelemetry.onWidgetClick(this.context, this.widgetPropsForTelemetry, { linkDescription: "ChartClicked" });

                        if (tempWindow != null) {
                            this.navigateToWorkItemList(clickEvent.stateName || "", clickEvent.iteration, workItemIds, tempWindow);
                        }
                    })
                    .then(undefined, error => {
                        WidgetTelemetry.onWidgetFailure(
                            this.context,
                            this.widgetPropsForTelemetry,
                            "Failure handling chart click in Velocity Chart.",
                            "handleChartClick",
                            { ErrorDetail: JSON.stringify(error) }
                        );
                    });
            }
        }
    }

    private async getWorkItemTypes(velocitySettings: VelocitySettings): Promise<string[]> {
        if (velocitySettings.workItemTypeFilter.identifier === WorkItemTypeFilterMode[WorkItemTypeFilterMode.BacklogCategory]) {
            const cacheableQueryService = this.context.getService<ICacheableQueryService>(IDashboardsCacheableQueryServiceName);
            const processesQuery = ProcessesQuery.onTeam(this.context, velocitySettings.projectId, velocitySettings.teamId);

            const workItemTypes = await cacheableQueryService.getCacheableQueryResult(processesQuery);
            const backlogCategory = velocitySettings.workItemTypeFilter.settings;
            return workItemTypes
                .map(entry => {
                    return entry.BacklogCategoryReferenceName === backlogCategory ? entry.WorkItemType : "";
                })
                .filter(o => o != "");
        } else {
            return Promise.resolve([velocitySettings.workItemTypeFilter.settings]);
        }
    }

    private getIterations(projectId: string, teamId: string, desiredIterationCount: number, dataQualityWarnings: Set<string>): Promise<Iteration[]> {
        const iterationsQuery = IterationsQuery.onTeam(projectId, teamId, desiredIterationCount);
        return this.runQuery<Iteration[]>("getIterations", iterationsQuery, undefined, dataQualityWarnings);
    }

    private getMetastate(
        iterationIds: string[],
        projectId: string,
        teamId: string,
        aggregation: ModefulValueSetting<AggregationMode, string>,
        workItemTypes: string[],
        dataQualityWarnings?: Set<string>
    ): Promise<Metastate[]> {
        const metastateQuery = new MetastateByIterationsQuery(this.command, projectId, teamId, iterationIds, aggregation, workItemTypes);
        return this.runQuery<Metastate[]>("getMetastate", metastateQuery, undefined, dataQualityWarnings);
    }

    private getCompletedLateMetastate(
        iterations: Iteration[],
        projectId: string,
        teamId: string,
        aggregation: ModefulValueSetting<AggregationMode, string>,
        workItemTypes: string[],
        showResolvedItemsAsCompleted = false,
        delay: number,
        dataQualityWarnings?: Set<string>
    ): Promise<Metastate[]> {
        const completedLateQuery = new CompletedLateMetastateByIterationsQuery(
            this.command,
            projectId,
            teamId,
            iterations,
            aggregation,
            workItemTypes,
            showResolvedItemsAsCompleted,
            delay
        );
        return this.runQuery<Metastate[]>("getCompletedLateMetastate", completedLateQuery, undefined, dataQualityWarnings);
    }

    private getPlannedWorkByDay(
        projectId: string,
        teamId: string,
        iterations: Iteration[],
        aggregation: ModefulValueSetting<AggregationMode, string>,
        workItemTypes: string[],
        delay: number,
        dataQualityWarnings?: Set<string>
    ): Promise<Work[]> {
        const plannedWorkQuery = new PlannedWorkByDayQuery(this.command, projectId, teamId, iterations, aggregation, workItemTypes, delay);
        return this.runQuery<Work[]>("getPlannedWorkByDay", plannedWorkQuery, undefined, dataQualityWarnings);
    }

    private getIncompleteMetastateDetails(
        projectId: string,
        teamId: string,
        iteration: Iteration,
        workItemTypes: string[],
        showResolvedItemsAsCompleted = false,
        dataQualityWarnings?: Set<string>
    ): Promise<WorkItemWithState[]> {
        const metastateQuery = new MetastateByIterationDetailsQuery(this.command, iteration.IterationSK || "", projectId, teamId, workItemTypes);
        return this.runQuery<WorkItemWithState[]>(
            "getMetastate",
            metastateQuery,
            metaWork => {
                return Promise.resolve(metaWork.filter(wi => VelocityDataHelper.isWorkItemInProgress(wi.StateCategory, wi.State, showResolvedItemsAsCompleted)));
            },
            dataQualityWarnings
        );
    }

    private getCompletedLateMetastateDetails(
        projectId: string,
        teamId: string,
        iteration: Iteration,
        workItemTypes: string[],
        showResolvedItemsAsCompleted = false,
        delay: number,
        dataQualityWarnings?: Set<string>
    ): Promise<WorkItemWithState[]> {
        const completedLateQuery = new CompletedLateMetastateByIterationDetailsQuery(
            this.command,
            iteration,
            projectId,
            teamId,
            workItemTypes,
            showResolvedItemsAsCompleted,
            delay
        );
        return this.runQuery<WorkItemWithState[]>("getCompletedLateMetastate", completedLateQuery, undefined, dataQualityWarnings);
    }

    private getPlannedWorkByDayDetails(
        projectId: string,
        teamId: string,
        iteration: Iteration,
        workItemTypes: string[],
        delay: number,
        dataQualityWarnings?: Set<string>
    ): Promise<WorkItem[]> {
        const plannedWorkQuery = new PlannedWorkByDayDetailsQuery(this.command, projectId, teamId, iteration, workItemTypes, delay);
        return this.runQuery<WorkItemWithState[]>("getPlannedWorkByDay", plannedWorkQuery, undefined, dataQualityWarnings);
    }

    private getAggregationWorkItemTypeFields(projectId: string): Promise<WorkItemTypeField[]> {
        const aggregationWorkItemTypeFieldsQuery = new AggregationWorkItemTypeFieldsQuery(this.command, projectId);
        return this.runQuery<WorkItemTypeField[]>("getAggregationWorkItemTypeFields", aggregationWorkItemTypeFieldsQuery);
    }

    private getCompletedMetastateDetails(
        projectId: string,
        teamId: string,
        iteration: Iteration,
        workItemTypes: string[],
        showResolvedItemsAsCompleted = false,
        lateWorkDelay?: number,
        dataQualityWarnings?: Set<string>
    ): Promise<WorkItemWithState[]> {
        const metastateQuery = new MetastateByIterationDetailsQuery(this.command, iteration.IterationSK || "", projectId, teamId, workItemTypes);
        let lateWorkPromise: Promise<WorkItemWithState[]> = Promise.resolve([]);
        if (lateWorkDelay != null) {
            lateWorkPromise = this.getCompletedLateMetastateDetails(projectId, teamId, iteration, workItemTypes, showResolvedItemsAsCompleted, lateWorkDelay);
        }
        return this.runQuery<WorkItemWithState[]>(
            "getMetastate",
            metastateQuery,
            (metaWork: WorkItemWithState[]) => {
                return lateWorkPromise.then(lateWork => {
                    const completedWork = metaWork.filter(wi => VelocityDataHelper.isWorkItemCompleted(wi.StateCategory, wi.State, showResolvedItemsAsCompleted));
                    const diff = this.arraySubtract(completedWork, lateWork);
                    return diff;
                });
            },
            dataQualityWarnings
        );
    }

    private runQuery<T>(
        name: string,
        query: AnalyticsCacheableQueryBase<any>,
        thenFunction?: (result: T) => Promise<T>,
        dataQualityWarnings?: Set<string>
    ): Promise<T> {
        if (!thenFunction) {
            thenFunction = (x: T) => Promise.resolve(x);
        }
        const resultPromise = WidgetTelemetry.executeAndTimeAsync(
            this.context,
            VelocityConstants.featureName,
            name,
            () => this.dataService.getCacheableQueryResult<T>(query).then(thenFunction),
            extractODataError
        );
        this.addDataQualityWarnings(this.dataService.getDataQualityWarnings<T>(query), dataQualityWarnings);

        return resultPromise;
    }

    private navigateToWorkItemList(metastate: string, iteration: Iteration, workItemIds: string[], tempWindow: Window) {
        const template =
            `SELECT [${CoreFieldRefNames.Id}],[${CoreFieldRefNames.WorkItemType}],[${CoreFieldRefNames.Title}] ` +
            `FROM WorkItems WHERE [${CoreFieldRefNames.Id}] IN ({0})`;
        const wiql = format(template, workItemIds.join(","));

        const tempQueryName = `${this.title} - ${iteration.IterationName} - ${metastate}`;

        this.navigateToQueryPage(wiql, tempQueryName, tempWindow);
    }

    private navigateToQueryPage(wiql: string, queryName: string, tempWindow: Window): void {
        new WiqlURLBuilder(this.context).BuildQueryURL(wiql, queryName, this.teamContext.id).then(url => {
            tempWindow.location.href = url;
        });
    }

    private arraySubtract(arr1: WorkItemWithState[], arr2: WorkItemWithState[]) {
        return arr1.filter(x => {
            return arr2.findIndex(y => y.WorkItemId === x.WorkItemId) < 0;
        });
    }

    private addDataQualityWarnings(dataQualityPromise: Promise<string[]>, dataQualityWarnings?: Set<string> | undefined): void {
        if (dataQualityWarnings) {
            dataQualityPromise.then(res => {
                res.forEach(warning => {
                    dataQualityWarnings.add(warning);
                });
            });
        }
    }
}
